/**
 * @fileoverview RecordingContext provides shared state and configuration for the recording system.
 * 
 * This context manages the recording session lifecycle, including:
 * - Session identification (installSid, examSid, sessionSid)
 * - Recording file availability polling and detection
 * - API client initialization with authentication
 * - Recording playback readiness state
 * - Canvas dimensions (configurable via API response, with env constant fallbacks)
 * 
 * The provider polls the backend to detect when the HLS recording file (m3u8) becomes
 * available, then updates the session and creates an annotation to signal readiness.
 * 
 * Canvas dimensions are resolved in this context so all child components can access
 * the correct size via `useRecordingContext()`. The Recording route fetches dimensions
 * from the API and passes them as props; the provider falls back to
 * CANVAS_WIDTH (1920) and CANVAS_HEIGHT (1080) from constants when no API values are available.
 * 
 * Key Features:
 * - Automatic polling for recording file availability (1s intervals)
 * - Max retry limit (60 attempts) to prevent infinite polling
 * - API client with authentication token management
 * - Recording start date synchronization
 * - Annotation creation when recording becomes available
 * - Centralized canvas dimension resolution (API → env constants)
 * 
 * @module RecordingContext
 */
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import ApiClient from '../../lib/ApiClient';
import { useLogger } from '@ui/shared/components/Logger';
import { RECORDING_START_CHECK_INTERVAL, LOGGER_LABEL, CANVAS_WIDTH, CANVAS_HEIGHT } from './utils/constants';

/**
 * Maximum number of retry attempts for checking recording file availability.
 * At 1 second intervals, this equals 60 seconds of polling.
 * @constant {number}
 */
const MAX_RETRY_ATTEMPTS = 60;

/**
 * React Context for recording session state and configuration.
 * 
 * @typedef {object} RecordingContextValue
 * @property {string|null} installSid - Installation identifier
 * @property {string|null} examSid - Exam identifier
 * @property {string|null} sessionSid - Session identifier
 * @property {boolean} showTrackSid - Whether to display track SID overlays for debugging
 * @property {boolean} recordingPlaybackReady - Whether the HLS recording file is available
 * @property {string|null} spMediaAuthToken - Authentication token for media API calls
 * @property {ApiClient|null} apiClient - Initialized API client instance
 * @property {number} canvasWidth - Recording canvas width in pixels
 * @property {number} canvasHeight - Recording canvas height in pixels
 */
export const RecordingContext = createContext({
  installSid: null,
  examSid: null,
  sessionSid: null,
  showTrackSid: false,
  recordingPlaybackReady: false,
  spMediaAuthToken: null,
  apiClient: null,
  canvasWidth: CANVAS_WIDTH,
  canvasHeight: CANVAS_HEIGHT,
});

/**
 * Custom hook to access the RecordingContext.
 * 
 * Provides access to recording session state and configuration including
 * session identifiers, API client, and recording playback readiness.
 * 
 * @returns {RecordingContextValue} The recording context value
 * @example
 * const { sessionSid, apiClient, recordingPlaybackReady } = useRecordingContext();
 */
export const useRecordingContext = () => useContext(RecordingContext);

/**
 * RecordingProvider component that provides recording context to child components.
 * 
 * This provider manages the recording session lifecycle by:
 * 1. Initializing an API client with authentication
 * 2. Polling for recording file (m3u8) availability
 * 3. Creating an annotation when recording becomes available
 * 4. Updating the session with the recording start date
 * 
 * Polling Strategy:
 * - Checks immediately on mount
 * - Polls every 1 second (RECORDING_START_CHECK_INTERVAL)
 * - Stops after 60 attempts (MAX_RETRY_ATTEMPTS)
 * - Stops when recording file is detected
 * 
 * @param {object} props - Component props
 * @param {string} props.installSid - Installation identifier
 * @param {string} props.examSid - Exam identifier
 * @param {string} props.sessionSid - Session identifier
 * @param {string} props.spMediaAuthToken - Authentication token for API calls
 * @param {boolean} [props.showTrackSid=false] - Whether to show track SID overlays for debugging
 * @param {number} [props.canvasWidth] - Canvas width from API response
 * @param {number} [props.canvasHeight] - Canvas height from API response
 * @param {React.ReactNode} props.children - Child components that will have access to context
 * @returns {React.ReactElement} Provider component wrapping children
 */
export const RecordingProvider = ({
  installSid,
  examSid,
  sessionSid,
  spMediaAuthToken,
  showTrackSid = false,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
  children,
}) => {
  const logger = useLogger();

  /**
   * Memoized API client instance initialized with authentication token.
   * Returns null if token is missing to prevent API call failures.
   * @type {ApiClient|null}
   */
  const apiClient = useMemo(() => {
    if (!spMediaAuthToken) {
      logger.error(LOGGER_LABEL, 'Missing spMediaAuthToken, API calls will fail');
      return null;
    }
    logger.debug(LOGGER_LABEL, 'API client initialized');
    return new ApiClient({ spToken: spMediaAuthToken });
  }, [spMediaAuthToken, logger]);

  /**
   * State indicating whether the recording file is available for playback.
   * Set to true when the m3u8 file is detected on the backend.
   * @type {boolean}
   */
  const [recordingPlaybackReady, setRecordingPlaybackReady] = useState(false);

  /**
   * State indicating whether maximum retry attempts have been reached.
   * Set to true after 60 failed attempts to prevent infinite polling.
   * @type {boolean}
   */
  const [maxRetriesReached, setMaxRetriesReached] = useState(false);

  /**
   * Ref to the interval ID for recording file availability polling.
   * Cleared when recording is detected or max retries reached.
   * @type {React.RefObject<number>}
   */
  const checkRecordingIntervalRef = useRef(null);

  /**
   * Ref tracking the current number of retry attempts.
   * Incremented on each check, compared against MAX_RETRY_ATTEMPTS.
   * @type {React.RefObject<number>}
   */
  const retryCountRef = useRef(0);

  /**
   * Ref tracking whether a check is currently in progress.
   * Prevents concurrent API calls and race conditions.
   * @type {React.RefObject<boolean>}
   */
  const checkInProgressRef = useRef(false);

  // Log provider initialization
  useEffect(() => {
    logger.info(LOGGER_LABEL, 'Initializing RecordingProvider', {
      installSid,
      examSid,
      sessionSid,
      hasToken: !!spMediaAuthToken,
      showTrackSid
    });
  }, [installSid, examSid, sessionSid, spMediaAuthToken, showTrackSid, logger]);

  /**
   * Checks if the recording m3u8 file is available on the backend.
   * 
   * This function:
   * 1. Calls the API to check file availability
   * 2. If available, sets recordingPlaybackReady to true
   * 3. Creates an annotation to signal availability
   * 4. Updates the session with the recording start date
   * 5. Clears the polling interval
   * 
   * Called immediately on mount and then every 1 second until:
   * - Recording file is detected, OR
   * - Max retry attempts (60) are reached
   * 
   * @async
   * @function checkRecordingFileAvailability
   * @returns {Promise<void>}
   * @private
   */
  const checkRecordingFileAvailability = async () => {
    if (!sessionSid || recordingPlaybackReady) return;

    if (!apiClient) {
      logger.error(LOGGER_LABEL, 'Cannot check recording: API client not available');
      return;
    }

    // Prevent concurrent requests - critical for avoiding race conditions
    if (checkInProgressRef.current) {
      logger.debug(LOGGER_LABEL, 'Check already in progress, skipping', {
        retryCount: retryCountRef.current
      });
      return;
    }

    // Mark check as in progress
    checkInProgressRef.current = true;

    try {
      retryCountRef.current++;

      // Endpoint should return information about the m3u8 file availability
      const response = await apiClient.checkRecordingFileAvailability({
        installSid,
        examSid,
        sessionSid,
        spMediaAuthToken,
      });

      if (response.successful && response.data?.startDate) {
        // Double-check state hasn't changed while request was in-flight
        if (recordingPlaybackReady) {
          logger.debug(LOGGER_LABEL, 'Recording already marked ready, skipping setup', {
            startDate: response.data.startDate
          });
          return;
        }

        logger.info(LOGGER_LABEL, 'Recording file is now available', {
          startDate: response.data.startDate,
          retriesNeeded: retryCountRef.current
        });

        // Signal that recording playback is ready to go...
        setRecordingPlaybackReady(true);

        // Clear the interval immediately to prevent new requests
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
          logger.debug(LOGGER_LABEL, 'Polling interval cleared');
        }

        // Use Promise.allSettled to ensure both operations complete even if one fails
        // This prevents partial state updates and ensures consistency
        const results = await Promise.allSettled([
          // Add annotation to signal availability
          apiClient.addAnnotation({
            installSid,
            examSid,
            sessionSid,
            payload: {
              typeId: 'recording-file-available',
              createdById: 'Session Recorder',
              metadata: {
                startDate: response.data.startDate,
              },
            },
          }),
          // Update session with recording start date
          apiClient.patchSession({
            installSid,
            examSid,
            sessionSid,
            recordingStartDate: response.data.startDate,
            status: 'Onboarding',
          })
        ]);

        // Log results of both operations
        const [annotationResult, sessionResult] = results;

        if (annotationResult.status === 'fulfilled') {
          logger.info(LOGGER_LABEL, 'Successfully added recording file available annotation');
        } else {
          logger.error(LOGGER_LABEL, 'Failed to add recording file available annotation', {
            error: annotationResult.reason,
            startDate: response.data.startDate
          });
        }

        if (sessionResult.status === 'fulfilled') {
          logger.info(LOGGER_LABEL, 'Successfully updated recording start date');
        } else {
          logger.error(LOGGER_LABEL, 'Failed to update recording start date', {
            error: sessionResult.reason,
            startDate: response.data.startDate
          });
        }
      } else {
        logger.debug(LOGGER_LABEL, 'Recording file not available yet', {
          retryCount: retryCountRef.current,
          willRetryIn: RECORDING_START_CHECK_INTERVAL
        });
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error checking recording file availability', {
        error,
        retryCount: retryCountRef.current,
        installSid,
        examSid,
        sessionSid
      });
    } finally {
      // Always clear the in-progress flag, even if an error occurred
      checkInProgressRef.current = false;
    }
  };

  /**
   * Effect that manages polling for recording file availability.
   * 
   * Lifecycle:
   * 1. Checks immediately on mount
   * 2. Sets up interval to check every 1 second
   * 3. Stops polling when:
   *    - Recording file is detected (recordingPlaybackReady = true)
   *    - Max retries reached (60 attempts)
   *    - Component unmounts
   * 
   * Dependencies:
   * - sessionSid: Required to perform checks
   * - recordingPlaybackReady: Stops polling when true
   * - maxRetriesReached: Stops polling when true
   * 
   * @effect
   */
  useEffect(() => {
    if (!sessionSid) {
      logger.warn(LOGGER_LABEL, 'Missing sessionSid, cannot check recording availability');
      return;
    }

    if (maxRetriesReached) {
      logger.error(LOGGER_LABEL, 'Max retries reached, stopping checks');
      return;
    }

    // Clear any existing interval before creating new one
    if (checkRecordingIntervalRef.current) {
      clearInterval(checkRecordingIntervalRef.current);
      checkRecordingIntervalRef.current = null;
    }

    // Check immediately when component mounts
    checkRecordingFileAvailability();

    // If not set, poll every interval
    checkRecordingIntervalRef.current = setInterval(() => {
      if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
        logger.error(LOGGER_LABEL, 'Max retry attempts reached', {
          attempts: retryCountRef.current
        });
        setMaxRetriesReached(true);
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
        }
        return;
      }

      if (!recordingPlaybackReady) {
        checkRecordingFileAvailability();
      } else {
        if (checkRecordingIntervalRef.current) {
          clearInterval(checkRecordingIntervalRef.current);
          checkRecordingIntervalRef.current = null;
        }
      }
    }, RECORDING_START_CHECK_INTERVAL);

    // Clean up interval on unmount
    return () => {
      if (checkRecordingIntervalRef.current) {
        clearInterval(checkRecordingIntervalRef.current);
        checkRecordingIntervalRef.current = null;
      }
    };

  }, [sessionSid, recordingPlaybackReady, maxRetriesReached]);

  return (
    <RecordingContext.Provider
      value={{
        installSid,
        examSid,
        sessionSid,
        showTrackSid,
        recordingPlaybackReady,
        spMediaAuthToken,
        apiClient,
        canvasWidth,
        canvasHeight,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
};
